import type { Location } from 'history';
import { fromPairs, isEqual, omit, pick, set } from 'lodash/fp';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import {
  matchPath,
  Navigate,
  NavigateFunction,
  Route,
  useLocation,
  useNavigate,
} from 'react-router-dom-v5-compat';
import { change, getFormValues, isDirty } from 'redux-form';

import ConnectedInquiryTemplateWorkflowRuns from 'dashboard/apps/InquiryTemplateEditor/ConnectedInquiryTemplateWorkflowRuns';
import { MissingTranslationsMap } from 'dashboard/apps/InquiryTemplateEditor/forms/FormSectionTranslationsConfig/FormSectionTranslationsConfig';
import InquiryTemplateEditor from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor';
import {
  CONFIG_EDITOR_PATH,
  CONFIG_EDITOR_RELATIVE_PATH,
  EDITOR_ROOT_PATH,
  FLOW_EDITOR_BASE_PATH,
  FLOW_EDITOR_MILESTONE1_RELATIVE_PATH,
  FLOW_EDITOR_NODE_TOKEN_PATH,
  FLOW_EDITOR_RELATIVE_PATH,
  FLOW_EDITOR_ROUTE_INDEX_PATH,
  INQUIRY_TEMPLATE_EDITOR_FORM_NAME,
  REVIEW_CHANGES_PATH,
  REVIEW_CHANGES_RELATIVE_PATH,
  SCREEN_EDITOR_BASE_PATH,
  SCREEN_EDITOR_RELATIVE_PATH,
  THEME_EDITOR_PATH,
  THEME_EDITOR_RELATIVE_PATH,
  WORKFLOW_EDITOR_BASE_PATH,
  WORKFLOW_EDITOR_RELATIVE_PATH,
  WORKFLOW_RUNS_RELATIVE_PATH,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/constants';
import { getWorkflowSchemaLiveUpdated } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/selectors/form';
import type {
  FormFieldDirtyMap,
  HandleSaveInquiryTemplateVersionProps,
  InquiryTemplateEditorFormData,
  ITEFormDataKey,
  LoadValidationErrorsFormData,
  UploadedAsset,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/types';
import { InquiryTemplateVersionFormData } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/types';
import InquiryTemplateHeader, {
  PendingStates,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateHeader';
import {
  ComponentValidationError,
  ComponentValidationErrorsMap,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/component-validations/types';
import validateComponentConfigLogicRules from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/component-validations/UiLogicComponentValidation';
import { getComponentConfigValidationErrors } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/validations';
import InquiryTemplateTheme from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateTheme';
import { VersionDiff } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateVisualVersionDiff/types';
import MissingTranslationsModal from 'dashboard/apps/InquiryTemplateEditor/MissingTranslationsModal';
import PublishInquiryTemplateVersionModal from 'dashboard/apps/InquiryTemplateEditor/PublishInquiryTemplateVersionModal';
import SaveInquiryTemplateVersionModal from 'dashboard/apps/InquiryTemplateEditor/SaveInquiryTemplateVersionModal';
import {
  stepsWorkflowVersionTargets,
  uiStepAllComponentConfigs,
} from 'dashboard/apps/InquiryTemplateEditor/utils/steps';
import LoadingModal from 'dashboard/components/LoadingModal';
import ModalWrapper from 'dashboard/components/ModalWrapper';
import { SentryRoutes } from 'dashboard/components/SentryRoute/SentryRoute';
import LoadingIndicator from 'dashboard/components-lib/LoadingIndicator';
import NavConfirmPrompt from 'dashboard/components-lib/NavConfirmPrompt';
import NullState from 'dashboard/components-lib/NullState';
import { Backdrop } from 'dashboard/layouts/ShowViewLayoutV2';
import SessionClientDashboard from 'dashboard/lib/clients/SessionClientDashboard';
import { InquiryTemplateVersionError } from 'dashboard/lib/interfaces/InquiryTemplateVersionErrors';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { validateWorkflowConfiguration } from 'dashboard/lib/workflows/validation';
import { Environment } from 'dashboard/models/environment';
import { InquiryTemplate } from 'dashboard/models/inquiry-template';
import {
  InquiryTemplateModule,
  InquiryTemplateModuleOption,
  ModuleOptionKey,
  ModulePreviewMap,
} from 'dashboard/models/inquiry-template-module';
import {
  InquiryTemplateVersion,
  InquiryTemplateVersionCombinedStep,
  InquiryTemplateVersionStatus,
  InquiryTemplateVersionStep,
  InquiryTemplateVersionStepUi,
  InquiryTemplateVersionTier,
  isUiStepConfig,
  PartialInquiryTemplateVersion,
} from 'dashboard/models/inquiry-template-version';
import { Organization, ProductFeatureName } from 'dashboard/models/organization';
import { VerificationTemplate } from 'dashboard/models/verification-template';
import { VerificationTemplateVersion } from 'dashboard/models/verification-template-version';
import { WorkflowConfigError, WorkflowSchema } from 'dashboard/models/workflow';
import { WorkflowStepInput, WorkflowVersion } from 'dashboard/models/workflow-version';
import { showMessage } from 'dashboard/redux/actions/messages';
import { useListExternalIntegrationVendorsQuery } from 'dashboard/redux/api/external-integration-vendor';
import { getAllWorkflowValidationContexts } from 'dashboard/redux/selectors/fle-workflow-context';
import { isThemeEditorFormDirty } from 'dashboard/redux/selectors/theme';
import {
  InquiryTemplateSchema,
  InquiryTemplateTreeData,
} from 'dashboard/redux/slices/inquiry-template';
import { RootState } from 'dashboard/redux/store';
import { postProcessVersion } from 'dashboard/redux/thunk-actions/verification-template';
import {
  getFormPathsForSubmission,
  getLoadingModalTitle,
  getUpdatedVtmplVersionPath,
  getUpdatedWorkflowEditorPath,
  isReadyForValidation,
  ReferencedIdTuple,
  simplifyStepConfigTransitions,
  stepsWithSanitizedBackSteps,
  updatedStepVtmplvReferences,
  updatedWorkflowVersionReferences,
  updatedWorkflowVersionVtmplvReferences,
} from 'dashboard/views/DashboardInquiryTemplatesShowView/utils';
import { InquiryIso6391LanguageCode } from 'lib/constants/locales';
import { FieldSchema } from 'lib/fields';
import { useExpandCollapseNavbar } from 'lib/hooks/useExpandCollapseNavbar';
import useFeatureFlag from 'lib/hooks/useFeatureFlag';
import useIsPersonerd from 'lib/hooks/useIsPersonerd';
import {
  useKeydownRedoListener,
  useKeydownSaveListener,
  useKeydownUndoListener,
} from 'lib/hooks/useKeyListener';
import useProductFeature from 'lib/hooks/useProductFeature';
import useToggle from 'lib/hooks/useToggle';
import { ApiEndpoint } from 'lib/interfaces/api';
import { StepPreview, UiStepType } from 'lib/interfaces/StepsConfig';
import { DeepPartial } from 'lib/types';
import { nullthrows } from 'lib/utils/nullthrows';
import { getOnlyLastPath } from 'lib/utils/url';
// we are adding this id to an div because it serves as an anchor for createPortal functions downstream,
// we need to use createPortal for some of our componenets otherwise they do not render properly
// in Safari
export const INQUIRY_TEMPLATE_EDITOR_SHOW_VIEW_ID = 'inquiry_template_editor_show_view_id';

const form = INQUIRY_TEMPLATE_EDITOR_FORM_NAME;

/**
 * We treat template-only and version-only updates differently
 * within the same form to avoid unnecessary requests.
 */
const TEMPLATE_FORM_UPDATED_KEYS: Array<ITEFormDataKey> = ['template-name', 'template-description'];

interface DashboardInquiryTemplatesShowViewProps {
  inquiryTemplateTreeData: InquiryTemplateTreeData;
  stepsPreview: StepPreview[];
  inquiryTemplate: InquiryTemplate | undefined;
  organization: Organization;
  currentEnvironmentType: Environment['type'];
  versionDiff: VersionDiff | undefined;
  comparedPublishedVersion: InquiryTemplateVersion | undefined;
  partialInquiryTemplateVersions: PartialInquiryTemplateVersion[] | undefined;
  restoredFrom: string | undefined;
  localPartialInquiryTemplateVersion: PartialInquiryTemplateVersion | undefined;
  locale: InquiryIso6391LanguageCode;
  comparedSchema: InquiryTemplateSchema;
  assets: UploadedAsset[];
  moduleOptions: InquiryTemplateModuleOption[];
  modulePreviewMap: ModulePreviewMap;
  pendingStates: PendingStates;
  loadingComparedVersion: boolean;
  loadingVersionDiff: boolean;
  loadingWorkflowSchema: boolean;
  redoButtonEnabled: boolean;
  undoButtonEnabled: boolean;
  isFormPristine: boolean;
  isFormValid: boolean;
  validationErrors: InquiryTemplateVersionError[];
  inputStep: WorkflowStepInput;
  handleSaveInquiryTemplateVersion: ({
    formValues,
    inquiryTemplate,
    reloadPath,
  }: HandleSaveInquiryTemplateVersionProps) => Promise<void>;
  handleSaveInquiryTemplate: ({
    formValues,
  }: {
    formValues: InquiryTemplateVersionFormData;
  }) => Promise<void>;
  handleDebouncedUpdateValidationErrors: (
    inquiryTemplateVersionId: string,
    formData: LoadValidationErrorsFormData,
  ) => void;
  handleSelectVersion: (inquiryTemplateVersionId: string) => void;
  handleAddLocalDraftVersion: () => void;
  handleLoadComparedVersion: () => void;
  handleLoadVersionDiff: () => void;
  handlePublishDraft: (shouldUpdateEnhancedBilling: boolean, lockVersion: number) => Promise<void>;
  handleDiscardDraft: (inquiryTemplateId: string, reloadCurrentVersion?: boolean) => Promise<void>;
  handleUpdateStoredVerificationTemplates: (verificationTemplates: VerificationTemplate[]) => void;
  handleUpdateVerificationTemplateVersions: (
    verificationTemplateVersions: (
      | VerificationTemplateVersion
      | DeepPartial<VerificationTemplateVersion>
    )[],
  ) => Promise<ReferencedIdTuple[]>;
  handleUpdateWorkflowVersions: (
    workflowVersions: WorkflowVersion[],
  ) => Promise<ReferencedIdTuple[]>;
  handleSetLocale: (locale: InquiryIso6391LanguageCode) => void;
  handleLoadPartialInquiryTemplateVersions: () => void;
  handleLoadModulePreview: (moduleKey: ModuleOptionKey) => Promise<void>;
  handleLoadModule: (
    moduleKey: ModuleOptionKey,
    currentFieldSchema: FieldSchema[],
  ) => Promise<InquiryTemplateModule>;
  handleLoadStepLocalizedStrings: (
    stepNames?: Array<string>,
    locales?: Array<InquiryIso6391LanguageCode>,
  ) => Promise<void>;
  handleHistoryInitialization: (formData: InquiryTemplateEditorFormData) => void;
  handleUndo: (navigate: NavigateFunction, numSteps?: number) => void;
  handleRedo: (numSteps?: number) => void;
  resetForm: () => void;
  submit: () => void;
}

const DashboardInquiryTemplatesShowView = ({
  inquiryTemplateTreeData,
  stepsPreview,
  inquiryTemplate,
  currentEnvironmentType,
  organization,
  versionDiff,
  comparedPublishedVersion,
  partialInquiryTemplateVersions,
  localPartialInquiryTemplateVersion,
  restoredFrom,
  locale,
  comparedSchema,
  moduleOptions,
  modulePreviewMap,
  pendingStates,
  loadingComparedVersion,
  loadingVersionDiff,
  loadingWorkflowSchema,
  redoButtonEnabled,
  undoButtonEnabled,
  isFormPristine,
  isFormValid,
  validationErrors,
  inputStep,
  handleAddLocalDraftVersion,
  handleSelectVersion,
  handleSaveInquiryTemplateVersion,
  handleSaveInquiryTemplate,
  handleDebouncedUpdateValidationErrors,
  handleLoadComparedVersion,
  handleLoadVersionDiff,
  handlePublishDraft,
  handleDiscardDraft,
  handleUpdateStoredVerificationTemplates,
  handleUpdateVerificationTemplateVersions,
  handleUpdateWorkflowVersions,
  handleSetLocale,
  handleLoadPartialInquiryTemplateVersions,
  handleLoadModulePreview,
  handleLoadModule,
  handleLoadStepLocalizedStrings,
  handleHistoryInitialization,
  handleRedo,
  handleUndo,
  resetForm,
  submit,
}: DashboardInquiryTemplatesShowViewProps) => {
  const isThemeSetFrontendEnabled = useFeatureFlag('dashboard:fle-theme-set-index-and-editor');
  const enableLeftNav = useFeatureFlag('dashboard:fle-redesign-left-nav-and-panels');
  const enableSavingInvalidItvDraft = useFeatureFlag('inquiry:enable-saving-invalid-itv-drafts');

  const { pathname } = useLocation();
  const lastPath = getOnlyLastPath(pathname);
  const [submissionWorkflowErrors, setSubmissionWorkflowErrors] = useState<Record<
    string,
    WorkflowConfigError[]
  > | null>(null);
  const [submissionComponentErrors, setSubmissionComponentErrors] =
    useState<ComponentValidationErrorsMap | null>(null);
  const hasLocalDraft = localPartialInquiryTemplateVersion != null;
  const latestPublishedVersionId =
    inquiryTemplate?.relationships?.['latest-published-version']?.data?.id;
  const showPublishDiffModal = latestPublishedVersionId != null;
  const enhancedUsageBillingEnabled = useProductFeature(ProductFeatureName.InquiriesEnhanced);
  const onReviewChangesPath = matchPath(
    { path: REVIEW_CHANGES_PATH, caseSensitive: false },
    pathname,
  );
  const allowSavingInvalidItvDraft = useFeatureFlag('inquiry:enable-saving-invalid-itv-drafts');

  const { inquiryTemplateVersion, schema } = inquiryTemplateTreeData;

  const [
    isShowingInquiryTemplateVersions,
    { disable: hideInquiryTemplateVersions, toggle: toggleVersionsPanel },
  ] = useToggle(false);

  const handleToggleVersionsPanel = useCallback(() => {
    // Check if the list of versions have already been loaded by checking if the current ITV is in the list.
    // We need to do this check to make sure that the list of versions get reloaded if the user switches which template they're viewing.
    const versionsWereLoaded =
      inquiryTemplateVersion?.id &&
      partialInquiryTemplateVersions?.some((pitv) => pitv.id === inquiryTemplateVersion.id);
    // Load the versions when opening the panel and versions has not already been loaded
    if (!isShowingInquiryTemplateVersions && !versionsWereLoaded) {
      handleLoadPartialInquiryTemplateVersions();
    }
    toggleVersionsPanel();
  }, [
    partialInquiryTemplateVersions,
    handleLoadPartialInquiryTemplateVersions,
    isShowingInquiryTemplateVersions,
    toggleVersionsPanel,
  ]);

  const [hasOpenSaveModal, { enable: showSaveModal, disable: hideSaveModal }] = useToggle(false);
  const navigate = useNavigate();
  // collapse dashboard navbar when opening editor for maximum
  // screen real estate. expand when exiting for better UX
  useExpandCollapseNavbar();

  const missingTranslationsRef = useRef<MissingTranslationsMap>({});
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const [currentPublishModal, setCurrentPublishModal] = useState<'translations' | 'confirm'>(null);
  const [refreshWorkflowSchema, setRefreshWorkflowSchema] = useState(false);

  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const hidePublishModal = () => setCurrentPublishModal(null);

  const handleRolloutAndPublish = useCallback(async () => {
    handlePublishDraft(
      // Note that, for simplicity, the template already being enhanced does not prevent
      // this value from being true
      enhancedUsageBillingEnabled &&
        inquiryTemplateVersion.attributes.tier === InquiryTemplateVersionTier.Enhanced,
      inquiryTemplateVersion.attributes['lock-version'],
    );
    hidePublishModal();

    // If we are on the review changes page, we need to redirect back to the previous page
    if (onReviewChangesPath) navigate(-1);
  }, [
    handlePublishDraft,
    enhancedUsageBillingEnabled,
    inquiryTemplateVersion?.attributes,
    onReviewChangesPath,
  ]);

  const editorFormValues = useSelector<RootState, any>(
    (state) => getFormValues(INQUIRY_TEMPLATE_EDITOR_FORM_NAME)(state) ?? {},
  );

  const workflowSchema = useSelector<RootState, WorkflowSchema>((state) =>
    getWorkflowSchemaLiveUpdated(state),
  );

  const workflowValidationContexts = useSelector(getAllWorkflowValidationContexts);

  const componentConfigsPassValidation = useCallback(() => {
    const clientComponentConfigErrors = getComponentConfigValidationErrors({
      steps: editorFormValues.steps,
    });
    const hasClientErrors = Object.entries(clientComponentConfigErrors).length > 0;

    if (hasClientErrors) {
      setSubmissionComponentErrors(clientComponentConfigErrors);
    }

    return !hasClientErrors;
  }, [editorFormValues.steps]);

  const { data: externalIntegrationVendors = [] } = useListExternalIntegrationVendorsQuery();
  const workflowsPassValidation = useCallback(() => {
    // cleans up action nodes where all steps are deleted in embedded editor but the node itself
    // is not deleted using hover controls, as the update_next action cannot be deleted via the UI
    const cleanedFormSteps = simplifyStepConfigTransitions(
      editorFormValues.steps,
      editorFormValues.workflowVersions,
    );

    // perform frontend workflow validations on all referenced workflow versions, not just
    // changed ones since updates to upstream values such as fields can invalidate workflow configs.
    // duplicated logic from InquiryTemplateEditor to prevent breaking redux form encapsulation
    const workflowValidationErrors = stepsWorkflowVersionTargets(cleanedFormSteps).map(
      (wfvId): [string, WorkflowConfigError[]] => {
        const wfv = editorFormValues.workflowVersions[wfvId];
        return [
          wfvId,
          validateWorkflowConfiguration({
            validationContext: workflowValidationContexts[wfvId],
            outputs: wfv.attributes.outputs,
            timeOutMinutes: wfv.attributes['time-out-minutes'],
            externalIntegrationVendors,
          }),
        ];
      },
    );

    const hasFrontendWorkflowValidationErrors = workflowValidationErrors.some(
      ([_wfvId, workflowErrors]) => workflowErrors.length > 0,
    );

    if (hasFrontendWorkflowValidationErrors) {
      setSubmissionWorkflowErrors(fromPairs(workflowValidationErrors));
    }

    return !hasFrontendWorkflowValidationErrors;
  }, [editorFormValues, workflowValidationContexts, externalIntegrationVendors]);

  const handlePublishWithPrompt = useCallback(async () => {
    if (!workflowsPassValidation() || !componentConfigsPassValidation()) return;

    if (onReviewChangesPath) {
      setCurrentPublishModal('confirm');
      return;
    }

    let missingTranslations: MissingTranslationsMap = {};
    try {
      const {
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        'missing-translations': currentMissingTranslations,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        'lock-version': missingTranslationsLockVersion,
      } = (
        await SessionClientDashboard.getResource<
          InquiryTemplateVersion,
          { 'missing-translations': MissingTranslationsMap; 'lock-version': number }
        >(ApiEndpoint.InquiryTemplateVersion, `${inquiryTemplateVersion?.id}/missing-translations`)
      ).meta;
      if (missingTranslationsLockVersion !== inquiryTemplateVersion.attributes['lock-version']) {
        dispatch(
          showMessage(
            'This template has been recently updated. Please refresh and try again.',
            MessageType.Failure,
          ),
        );
        return;
      }

      missingTranslations = currentMissingTranslations;
    } catch (e) {
      dispatch(showMessage('Failed to publish, please try again.', MessageType.Failure));
      return;
    }

    if (Object.keys(missingTranslations).length > 0) {
      missingTranslationsRef.current = missingTranslations;
      setCurrentPublishModal('translations');
    } else if (showPublishDiffModal) {
      handleLoadVersionDiff();
      navigate(basePath + '/review-changes');
    } else {
      setCurrentPublishModal('confirm');
    }
  }, [
    inquiryTemplateVersion?.id,
    onReviewChangesPath,
    showPublishDiffModal,
    componentConfigsPassValidation,
    handleLoadComparedVersion,
    workflowsPassValidation,
  ]);

  const initialValues = useMemo<InquiryTemplateEditorFormData>(
    () => ({
      'template-name': inquiryTemplate?.attributes?.name ?? '',
      'template-description': inquiryTemplate?.attributes?.description ?? '',
      'allowed-hosts': inquiryTemplateVersion?.attributes['allowed-hosts'] ?? [],
      'disable-client-create': inquiryTemplateVersion?.attributes['disable-client-create'],
      'disable-device-handoff-inputs-if-prefilled':
        inquiryTemplateVersion?.attributes['disable-device-handoff-inputs-if-prefilled'],
      'enable-device-handback': inquiryTemplateVersion?.attributes['enable-device-handback'],
      'enabled-locales': inquiryTemplateVersion?.attributes['enabled-locales'],
      'expiration-intervals': inquiryTemplateVersion?.attributes['expiration-intervals'],
      'max-session-limit': inquiryTemplateVersion?.attributes['max-session-limit'],
      'field-key-device-handoff-email':
        inquiryTemplateVersion?.attributes['field-key-device-handoff-email'] ?? '',
      'field-key-device-handoff-phone-number':
        inquiryTemplateVersion?.attributes['field-key-device-handoff-phone-number'] ?? '',
      'field-schema': inquiryTemplateVersion?.attributes['field-schema'],
      'has-reusable-persona-config':
        inquiryTemplateVersion?.attributes['has-reusable-persona-config'],
      'redirect-uri': inquiryTemplateVersion?.attributes['redirect-uri'] ?? '',
      'redirect-query-string-config':
        inquiryTemplateVersion?.attributes['redirect-query-string-config'],
      steps: inquiryTemplateVersion?.attributes?.steps,
      theme: inquiryTemplateVersion?.attributes?.theme,
      verificationTemplateVersions: schema.verificationTemplateVersions,
      workflowVersions: schema.workflowVersions,
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      matchRequirementProfiles: null,
      description: inquiryTemplateVersion?.attributes['description'],
      themeSetId: inquiryTemplateVersion?.relationships['theme-set']?.data?.id,
    }),
    [inquiryTemplateVersion, inquiryTemplate, schema],
  );

  useEffect(
    () => handleHistoryInitialization(initialValues),
    [handleHistoryInitialization, initialValues],
  );

  const isITVFieldDirty = (state: RootState, field: ITEFormDataKey) => {
    // Consider all fields dirty if restoring version
    return hasLocalDraft ? true : isDirty(form)(state, field);
  };

  const formFieldsDirtyMap = useSelector<RootState, FormFieldDirtyMap>(
    (state) => ({
      'template-name': isITVFieldDirty(state, 'template-name'),
      'template-description': isITVFieldDirty(state, 'template-description'),
      'allowed-hosts': isITVFieldDirty(state, 'allowed-hosts'),
      'disable-client-create': isITVFieldDirty(state, 'disable-client-create'),
      'disable-device-handoff-inputs-if-prefilled': isITVFieldDirty(
        state,
        'disable-device-handoff-inputs-if-prefilled',
      ),
      'enable-device-handback': isITVFieldDirty(state, 'enable-device-handback'),
      'enabled-locales': isITVFieldDirty(state, 'enabled-locales'),
      'expiration-intervals': isITVFieldDirty(state, 'expiration-intervals'),
      'max-session-limit': isITVFieldDirty(state, 'max-session-limit'),
      'field-key-device-handoff-email': isITVFieldDirty(state, 'field-key-device-handoff-email'),
      'field-key-device-handoff-phone-number': isITVFieldDirty(
        state,
        'field-key-device-handoff-phone-number',
      ),
      'field-schema': isITVFieldDirty(state, 'field-schema'),
      'has-reusable-persona-config': isITVFieldDirty(state, 'has-reusable-persona-config'),
      'redirect-uri': isITVFieldDirty(state, 'redirect-uri'),
      'redirect-query-string-config': isITVFieldDirty(state, 'redirect-query-string-config'),
      steps: isITVFieldDirty(state, 'steps'),
      theme: isITVFieldDirty(state, 'theme'),
      verificationTemplateVersions: isITVFieldDirty(state, 'verificationTemplateVersions'),
      workflowVersions: isITVFieldDirty(state, 'workflowVersions'),
      matchRequirementProfiles: isITVFieldDirty(state, 'matchRequirementProfiles'),
      description: isITVFieldDirty(state, 'description'),
      themeSetId: isITVFieldDirty(state, 'themeSetId'),
    }),
    shallowEqual,
  );

  const inquiryTemplateVersionUpdated = Object.keys(initialValues)
    .filter((k): k is ITEFormDataKey => !TEMPLATE_FORM_UPDATED_KEYS.includes(k as ITEFormDataKey))
    .some((field: ITEFormDataKey) => formFieldsDirtyMap[field]);

  const formObjectDirty = (
    initial:
      | VerificationTemplateVersion
      | DeepPartial<VerificationTemplateVersion>
      | WorkflowVersion,
    current:
      | VerificationTemplateVersion
      | DeepPartial<VerificationTemplateVersion>
      | WorkflowVersion,
  ): boolean => {
    if (initial === current) {
      return false;
    } else {
      return !isEqual(initial, current);
    }
  };

  const getChangedWorkflowVersions = (formData: Partial<InquiryTemplateEditorFormData>) => {
    const referencedWorkflowVersionIds = stepsWorkflowVersionTargets(formData.steps ?? []);
    return Object.values(formData.workflowVersions ?? {}).filter((wfv) =>
      !wfv.relationships.workflow?.data?.id
        ? // for newly added wfvs, check if wfv is referenced in a step
          referencedWorkflowVersionIds.includes(wfv.id)
        : formObjectDirty(wfv, initialValues.workflowVersions[wfv.id]),
    );
  };

  const validateCaptureMethods = (steps: InquiryTemplateVersionStep[]) => {
    const stepTypes = [UiStepType.GovernmentId, UiStepType.Selfie, UiStepType.Document];
    return steps
      .filter((step: InquiryTemplateVersionStep) => stepTypes.includes(step.type))
      .every(
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        (step: InquiryTemplateVersionCombinedStep) =>
          step.config['enabled-capture-options-desktop'].length > 0 &&
          step.config['enabled-capture-options-mobile'].length > 0 &&
          (step.type !== UiStepType.GovernmentId ||
            step.config['enabled-capture-options-native-mobile'].length > 0),
      );
  };

  const validateDeviceHandoffMethods = (steps: InquiryTemplateVersionStep[]) => {
    const stepTypes = [UiStepType.GovernmentId, UiStepType.Selfie, UiStepType.Document];
    return steps
      .filter((step: InquiryTemplateVersionStep) => stepTypes.includes(step.type))
      .every(
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        (step: InquiryTemplateVersionCombinedStep) =>
          (!step.config['enabled-capture-options-desktop'].includes('device_handoff') ||
            step.config['device-handoff-options'].length > 0) &&
          (!step.config['enabled-capture-options-mobile'].includes('device_handoff') ||
            step.config['device-handoff-options'].length > 0),
      );
  };

  const latestPartialInquiryTemplateVersion =
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    partialInquiryTemplateVersions[partialInquiryTemplateVersions.length - 1];
  let shownPartialInquiryTemplateVersions;

  if (!hasLocalDraft) {
    shownPartialInquiryTemplateVersions = partialInquiryTemplateVersions;
  } else if (
    latestPartialInquiryTemplateVersion.attributes.status === InquiryTemplateVersionStatus.Published
  ) {
    // Latest version is published; append local partial version to end
    shownPartialInquiryTemplateVersions = [
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      ...partialInquiryTemplateVersions,
      localPartialInquiryTemplateVersion,
    ];
  } else {
    // Latest version is draft; replace last with local partial version
    shownPartialInquiryTemplateVersions = [
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      ...partialInquiryTemplateVersions.slice(0, -1),
      localPartialInquiryTemplateVersion,
    ];
  }

  const dispatch = useDispatch();
  const isPersonerd = useIsPersonerd();

  const handleSave = () => {
    if (
      (!workflowsPassValidation() || !componentConfigsPassValidation()) &&
      !enableSavingInvalidItvDraft
    )
      // First validate workflows and screen components
      return;

    setSubmissionWorkflowErrors(null);
    setSubmissionComponentErrors(null);

    if (isPersonerd) {
      const {
        'field-schema': fieldSchema,
        steps,
        workflowVersions: workflowVersionMap,
      } = editorFormValues;
      const componentLogicErrors: ComponentValidationError[] = steps
        .filter(isUiStepConfig)
        .flatMap((step: InquiryTemplateVersionStepUi) =>
          uiStepAllComponentConfigs(step).flatMap((componentConfig) =>
            validateComponentConfigLogicRules({
              componentConfig,
              currentStep: step,
              steps,
              fieldSchema,
              workflowVersionMap,
            }),
          ),
        )
        .filter(Boolean);

      if (componentLogicErrors.length > 0) {
        const errorsDisplay = componentLogicErrors.map(({ message }) => message).join(`\n\n`);
        dispatch(
          showMessage(
            `Component logic rules may be invalid:\n\n${errorsDisplay}`,
            MessageType.Notice,
          ),
        );
      }
    }

    // If only the template has been updated (i.e. template name)
    // and therefore only the template needs saving, don't show the save
    // modal and instead submit immediately
    if (currentPublishModal === 'translations') {
      dispatch(change(form, 'description', 'Updated translations'));
      submit();
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      setCurrentPublishModal(null);
    } else if (inquiryTemplateVersionUpdated) {
      showSaveModal();
    } else {
      submit();
    }
  };

  const handleDiscard = useCallback(async () => {
    if (
      latestPartialInquiryTemplateVersion.attributes.status === InquiryTemplateVersionStatus.Draft
    ) {
      // Actually call discard if we are working on a draft that has been saved
      const reloadCurrentVersion =
        inquiryTemplateVersion?.id === latestPartialInquiryTemplateVersion.id;
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      await handleDiscardDraft(inquiryTemplate.id, reloadCurrentVersion);
    } else {
      // Otherwise, we are working on a local draft that has never been saved;
      // if we discard, we will discard a saved draft that we are not currently working on
      handleSelectVersion(latestPartialInquiryTemplateVersion.id);
    }
  }, [
    inquiryTemplate?.id,
    inquiryTemplateVersion?.id,
    latestPartialInquiryTemplateVersion?.attributes.status,
    latestPartialInquiryTemplateVersion?.id,
    handleDiscardDraft,
    handleSelectVersion,
  ]);

  const transformFormDataForSubmission = (formData: InquiryTemplateEditorFormData) => {
    // omit field-schema if pristine
    const omitPaths = formFieldsDirtyMap['field-schema'] ? [] : ['field-schema'];
    let submittedFormData = omit(omitPaths, formData) as Omit<
      InquiryTemplateEditorFormData,
      'field-schema'
    >;

    // If we restored from draft, we want to make sure any themes associated with the version
    // are also saved, as those themes are not on the editor form. Also, make sure we track
    // the restored from
    if (hasLocalDraft) {
      submittedFormData = set('theme', inquiryTemplateVersion.attributes.theme, submittedFormData);
      submittedFormData = set('restored-from', restoredFrom, submittedFormData);
    }

    // cleans up action nodes where all steps are deleted in embedded editor but the node itself
    // is not deleted using hover controls, as the update_next action cannot be deleted via the UI
    submittedFormData = set(
      'steps',
      simplifyStepConfigTransitions(submittedFormData.steps, submittedFormData.workflowVersions),
      submittedFormData,
    );

    return submittedFormData;
  };

  /**
   * Update the list of validation errors based on the current state of the templates after any updates the user has made.
   * @param formData form data that has the current state of the templates
   */
  const updateValidationErrors = (formData: InquiryTemplateEditorFormData) => {
    const submittedFormData = transformFormDataForSubmission(formData);

    const verificationTemplateVersions = formFieldsDirtyMap['verificationTemplateVersions']
      ? Object.values(submittedFormData.verificationTemplateVersions)
          .filter((vtmplv) =>
            formObjectDirty(
              vtmplv,
              initialValues.verificationTemplateVersions[vtmplv.id as string],
            ),
          )
          .map((vtmplv) => {
            return {
              ...vtmplv,
              attributes: postProcessVersion(
                nullthrows(vtmplv.attributes) as VerificationTemplateVersion['attributes'],
                nullthrows(vtmplv.type),
              ),
            } as VerificationTemplateVersion;
          })
      : [];
    const workflowVersions = formFieldsDirtyMap['workflowVersions']
      ? getChangedWorkflowVersions(submittedFormData)
      : [];
    const dirtyPathsExcludingVTVAndWFV = getFormPathsForSubmission(
      formFieldsDirtyMap,
      false,
      false,
    );

    const updatedFormData = {
      ...pick(dirtyPathsExcludingVTVAndWFV, submittedFormData),
      // manually add VTV and WFV changes here -- we only want the WFVs and VTVs that are dirty to reduce payload size
      verificationTemplateVersions: verificationTemplateVersions,
      workflowVersions: workflowVersions,
    } as LoadValidationErrorsFormData;

    if (inquiryTemplateVersionUpdated) {
      // TODO (eric): remove once back_step config is supported within ITE.
      // if ITV steps are updated, sanitize back_step references to disconnected steps before submitting
      // this is an interim state surfaced by the changes to omit steps and schema objects which are not
      // part of the inquiry flow from ITE. rather than allowing navigation to a 'new' screen in the flow
      // which is only accesible via that back button, remove the config entirely.
      const loadValidationErrorsFormData =
        // steps isn't always included as part of the ITV update, so sanitize conditionally
        updatedFormData.steps != null
          ? set(['steps'], stepsWithSanitizedBackSteps(updatedFormData.steps), updatedFormData)
          : updatedFormData;
      if (isReadyForValidation(loadValidationErrorsFormData)) {
        handleDebouncedUpdateValidationErrors(
          inquiryTemplateVersion.id,
          loadValidationErrorsFormData,
        );
      }
    }
  };

  /**
   * prevent stray form onSubmit props from leaking into updateInquiryTemplate call
   * template editor save mechanics:
   * - if currently on published version -> create new draft then update with changes
   * - if currently on draft version -> update draft with changes
   */
  const updateInquiryTemplate = async (formData: Readonly<InquiryTemplateEditorFormData>) => {
    let submittedFormData = transformFormDataForSubmission(formData);
    const vtmplVersionIdList: ReferencedIdTuple[] = [];
    const workflowVersionIdList: ReferencedIdTuple[] = [];

    if (!validateCaptureMethods(submittedFormData.steps)) {
      dispatch(
        showMessage(
          'One or more capture methods must be selected for each platform.',
          MessageType.Failure,
        ),
      );
      return;
    }

    if (!validateDeviceHandoffMethods(submittedFormData.steps)) {
      dispatch(
        showMessage('One or more device handoff methods must be selected.', MessageType.Failure),
      );
      return;
    }

    // first handle updates to verification template versions in order to update references in workflow versions and steps config
    if (formFieldsDirtyMap['verificationTemplateVersions']) {
      const verificationTemplateVersions = Object.values(
        submittedFormData.verificationTemplateVersions,
      ).filter((vtmplv) =>
        formObjectDirty(vtmplv, initialValues.verificationTemplateVersions[vtmplv.id as string]),
      );

      try {
        await handleUpdateVerificationTemplateVersions(verificationTemplateVersions).then(
          (idTuples) =>
            vtmplVersionIdList.push(
              ...idTuples.filter(({ originalId, updatedId }) => originalId !== updatedId),
            ),
        );
      } catch {
        return;
      }
    }

    if (vtmplVersionIdList.length > 0) {
      // update step vtmpl-version-id references
      submittedFormData = set(
        ['steps'],
        updatedStepVtmplvReferences(submittedFormData.steps, vtmplVersionIdList),
        submittedFormData,
      );

      // update workflow action vtmpl-version-id references
      const updatedWorkflowVersions = updatedWorkflowVersionVtmplvReferences(
        Object.values(submittedFormData.workflowVersions),
        vtmplVersionIdList,
      );

      updatedWorkflowVersions.forEach((workflowVersion) => {
        submittedFormData = set(
          ['workflowVersions', workflowVersion.id],
          workflowVersion,
          submittedFormData,
        );
      });
    }

    // workflow version references to vtmpl versions need to be updated even if no changes are made to the workflow versions
    if (
      formFieldsDirtyMap['verificationTemplateVersions'] ||
      formFieldsDirtyMap['workflowVersions']
    ) {
      try {
        await handleUpdateWorkflowVersions(getChangedWorkflowVersions(submittedFormData)).then(
          (idTuples) =>
            workflowVersionIdList.push(
              ...idTuples.filter(({ originalId, updatedId }) => originalId !== updatedId),
            ),
        );
      } catch {
        return;
      }
    }

    submittedFormData =
      // update steps config workflow-version-id references
      workflowVersionIdList.length > 0
        ? set(
            ['steps'],
            updatedWorkflowVersionReferences(submittedFormData.steps, workflowVersionIdList),
            submittedFormData,
          )
        : submittedFormData;

    let reloadPath = lastPath;
    if (matchPath({ path: WORKFLOW_EDITOR_BASE_PATH, end: false }, pathname)) {
      reloadPath = getUpdatedWorkflowEditorPath(
        pathname,
        submittedFormData.workflowVersions,
        workflowVersionIdList,
      );
    } else if (pathname.includes('verifications')) {
      reloadPath = getUpdatedVtmplVersionPath(pathname, vtmplVersionIdList);
    } else if (pathname.includes('fields')) {
      // reload to config editor verifications tab if current view when saving
      reloadPath = 'config/fields';
    } else if (pathname.includes('screens') && lastPath !== 'screens') {
      // append selected screen to path, if exists
      reloadPath = `screens/${lastPath}`;
    } else if (pathname.includes('flow')) {
      const nodeTokenMatch = matchPath(
        {
          path: FLOW_EDITOR_NODE_TOKEN_PATH,
          end: true,
        },
        pathname,
      );
      const routeIndexMatch = matchPath(
        {
          path: FLOW_EDITOR_ROUTE_INDEX_PATH,
        },
        pathname,
      );
      reloadPath = [
        'flow',
        nodeTokenMatch?.params.nodeToken || routeIndexMatch?.params.nodeToken,
        routeIndexMatch?.params.routeIndex,
      ]
        .filter((path) => path != null)
        .join('/');
    }

    const dirtyPaths = getFormPathsForSubmission(
      formFieldsDirtyMap,
      vtmplVersionIdList.length > 0,
      workflowVersionIdList.length > 0,
    );
    const updatedFormData = pick(dirtyPaths, submittedFormData);

    if (inquiryTemplateVersionUpdated) {
      // TODO (eric): remove once back_step config is supported within ITE.
      // if ITV steps are updated, sanitize back_step references to disconnected steps before submitting
      // this is an interim state surfaced by the changes to omit steps and schema objects which are not
      // part of the inquiry flow from ITE. rather than allowing navigation to a 'new' screen in the flow
      // which is only accesible via that back button, remove the config entirely.
      const versionFormData =
        // steps isn't always included as part of the ITV update, so sanitize conditionally
        updatedFormData.steps != null
          ? set(['steps'], stepsWithSanitizedBackSteps(updatedFormData.steps), updatedFormData)
          : updatedFormData;
      await handleSaveInquiryTemplateVersion({
        formValues: versionFormData,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        inquiryTemplate,
        lockVersion: inquiryTemplateVersion.attributes['lock-version'],
        reloadPath,
      });
    }

    if (formFieldsDirtyMap['template-name'] || formFieldsDirtyMap['template-description']) {
      await handleSaveInquiryTemplate({
        formValues: updatedFormData,
      });
    }
  };

  const basePath = `/dashboard/inquiry-templates/${inquiryTemplate?.id}/${inquiryTemplateVersion?.id}`;
  const themeHasUnsavedChanges = useSelector<RootState, boolean>(isThemeEditorFormDirty);

  const blockOnRouteChange = useCallback(
    (prevLocation: Location, nextLocation?: Location) => {
      const prevIsITE = matchPath({ path: EDITOR_ROOT_PATH, end: false }, prevLocation.pathname);
      const nextIsITE = matchPath(
        { path: EDITOR_ROOT_PATH, end: false },
        nextLocation?.pathname ?? '',
      );
      if (prevIsITE && nextIsITE) {
        const prevIsNonThemeEditor = [
          FLOW_EDITOR_BASE_PATH,
          SCREEN_EDITOR_BASE_PATH,
          CONFIG_EDITOR_PATH,
          WORKFLOW_EDITOR_BASE_PATH,
          REVIEW_CHANGES_PATH,
        ].some((path) => matchPath({ path, end: false }, prevLocation.pathname));
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        const nextIsThemeEditor = matchPath(THEME_EDITOR_PATH, nextLocation.pathname);
        // notify about discarding changes when navigating from non-theme to theme editor and the
        // form is dirty, as theme editor and the other editors are part of separate form components.
        if (prevIsNonThemeEditor) {
          return nextIsThemeEditor ? !isFormPristine : false;
        } else {
          const prevLastPath = getOnlyLastPath(prevLocation.pathname);
          const nextLastPath =
            nextLocation?.pathname != null ? getOnlyLastPath(nextLocation.pathname) : '';
          // a location change from theme editor where the last paths are the same
          // is almost certainly a result of saving a published ITV and creating a new version.
          // in this scenario, we want to re-initialize the form without displaying the nav prompt modal.
          if (prevLastPath === nextLastPath) {
            return prevLocation.pathname === nextLocation?.pathname;
          }
          // otherwise, notify about discarding changes when in theme editor and the form is dirty,
          // as each theme editor section (e.g color, buttons, etc) is a separate form.
          return themeHasUnsavedChanges;
        }
      }
      return !isFormPristine;
    },
    [isFormPristine, themeHasUnsavedChanges],
  );

  const { isLoading } = pendingStates;
  const processing = Object.values(pendingStates).some((pending) => pending);

  // key down shortcuts
  const keydownShortcutsEnabled =
    // disable shortcuts for all non-missing translations publish modals
    (currentPublishModal == null || currentPublishModal === 'translations') &&
    !hasOpenSaveModal &&
    !processing &&
    // Don't allow shortcuts in the theme editor, because save/undo do not actually
    // impact theme values, which are on a different form
    !pathname.includes('theme');

  const handleCloseTranslationsModal = () => {
    resetForm();
    hidePublishModal();
  };

  useKeydownSaveListener((e) => {
    if (keydownShortcutsEnabled && isFormValid && !(isFormPristine && !hasLocalDraft)) {
      e.preventDefault();
      return handleSave();
    }
  });

  // Note: Needs to be registered before undo shortcut because they both use
  // Ctrl + Z. Redo has the additional SHIFT key, but the key press isn't checked
  // because CTRL + Z for undo is already satisfied if this listener is placed after undo
  useKeydownRedoListener((e) => {
    if (keydownShortcutsEnabled && redoButtonEnabled) {
      e.preventDefault();
      return handleRedo();
    }
  });

  useKeydownUndoListener((e) => {
    if (keydownShortcutsEnabled && undoButtonEnabled) {
      e.preventDefault();
      return handleUndo(navigate);
    }
  });

  const isMissingTreeData = inquiryTemplateVersion == null || stepsPreview.length === 0;

  /**
   * Display loading indicator for any active pending state, excluding updates.
   * N.B. when making updates in ITE, this loading spinner logic does not render the spinner
   * until the updates have succeeded and the new objects have been fetched.
   *
   * When updates fail, the omitted spinner render allows us to avoid re-initializing the ITE
   * form and preserves the values so that the user can correct them and resubmit.
   */
  if (isLoading && isMissingTreeData) {
    return (
      <Backdrop>
        <LoadingIndicator fullscreen />
      </Backdrop>
    );
  } else if (inquiryTemplate == null || inquiryTemplateVersion == null || schema == null) {
    return (
      <Backdrop>
        <NullState
          message='We couldn’t find that Template.'
          backRoute='/dashboard/inquiry-templates'
        />
      </Backdrop>
    );
  }

  const workflowName = schema.workflowVersions[lastPath]
    ? schema.workflowVersions[lastPath].attributes['workflow-name']
    : null;

  const navPromptMessage = matchPath({ path: THEME_EDITOR_PATH, end: false }, pathname)
    ? 'Discard unsaved theme changes?'
    : 'Discard unsaved template changes?';

  // eslint-disable-next-line @persona-id/eqeq-null-undefined
  const hasOpenPublishModal = currentPublishModal !== null;

  return (
    <>
      <Helmet title={`${inquiryTemplate.attributes.name} - Templates - Inquiries`} />
      <LoadingModal title={getLoadingModalTitle(pendingStates)} isLoading={processing} />
      <NavConfirmPrompt
        message={navPromptMessage}
        confirmActionText='Discard'
        prevent={blockOnRouteChange}
      />
      <ModalWrapper
        shouldCloseOnEsc
        isOpen={hasOpenPublishModal}
        styleOverride={{
          content: {
            overflowY: 'hidden',
          },
        }}
        handleCloseModal={
          currentPublishModal === 'translations' ? handleCloseTranslationsModal : hidePublishModal
        }
      >
        {currentPublishModal === 'translations' ? (
          <MissingTranslationsModal
            inquiryTemplateVersion={inquiryTemplateVersion}
            missingTranslations={missingTranslationsRef.current}
            handleClose={handleCloseTranslationsModal}
            handleSave={handleSave}
            disableSave={!inquiryTemplateVersionUpdated}
          />
        ) : (
          <PublishInquiryTemplateVersionModal
            inquiryTemplateVersion={inquiryTemplateVersion}
            organization={organization}
            hidePublishModal={hidePublishModal}
            handleRolloutAndPublish={handleRolloutAndPublish}
          />
        )}
      </ModalWrapper>
      <ModalWrapper isOpen={hasOpenSaveModal} handleCloseModal={hideSaveModal}>
        <SaveInquiryTemplateVersionModal
          hasLocalDraft={hasLocalDraft}
          inquiryTemplateVersion={inquiryTemplateVersion}
          handleSave={submit}
          hideSaveModal={hideSaveModal}
        />
      </ModalWrapper>

      <SentryRoutes>
        {[
          CONFIG_EDITOR_RELATIVE_PATH,
          FLOW_EDITOR_RELATIVE_PATH,
          SCREEN_EDITOR_RELATIVE_PATH,
          WORKFLOW_EDITOR_RELATIVE_PATH,
          THEME_EDITOR_RELATIVE_PATH,
          WORKFLOW_RUNS_RELATIVE_PATH,
          REVIEW_CHANGES_RELATIVE_PATH,
        ].map((path) => (
          <Route
            key={path}
            path={path + '/*'}
            element={
              <InquiryTemplateHeader
                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                showStatusSticker={partialInquiryTemplateVersions?.length > 1}
                inquiryTemplate={inquiryTemplate}
                inquiryTemplateVersion={inquiryTemplateVersion}
                currentEnvironmentType={currentEnvironmentType}
                lastPath={lastPath}
                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                workflowName={workflowName}
                pendingStates={pendingStates}
                isFormPristine={isFormPristine && !hasLocalDraft}
                isFormValid={isFormValid}
                hasValidationErrors={enableSavingInvalidItvDraft && validationErrors.length > 0}
                enableSavingInvalidItvDraft={enableSavingInvalidItvDraft}
                isLoadingWorkflowSchema={loadingWorkflowSchema}
                handleDiscardDraft={handleDiscardDraft}
                handlePublishDraft={handlePublishWithPrompt}
                handleSave={handleSave}
                handleSelectVersion={handleSelectVersion}
                handleToggleVersionsPanel={handleToggleVersionsPanel}
              />
            }
          />
        ))}
      </SentryRoutes>

      <SentryRoutes>
        <Route
          path={THEME_EDITOR_RELATIVE_PATH + '/*'}
          element={
            !isThemeSetFrontendEnabled ? (
              <InquiryTemplateTheme
                inquiryTemplate={inquiryTemplate}
                inquiryTemplateVersion={inquiryTemplateVersion}
                currentLocale={locale}
                stepsPreview={stepsPreview}
                lastPath={lastPath}
                handleSaveInquiryTemplateVersion={handleSaveInquiryTemplateVersion}
                handleSetLocale={handleSetLocale}
              />
            ) : (
              <Navigate to='/dashboard/inquiry-templates' replace />
            )
          }
        />

        <Route
          path={WORKFLOW_RUNS_RELATIVE_PATH + '/*'}
          element={
            <ConnectedInquiryTemplateWorkflowRuns
              workflowVersions={Object.values(schema.workflowVersions)}
            />
          }
        />

        {/* Redirect if left nav is disabled */}
        {!enableLeftNav && (
          <Route
            path={FLOW_EDITOR_MILESTONE1_RELATIVE_PATH}
            element={<Navigate to='/dashboard/inquiry-templates' replace />}
          />
        )}

        <Route
          path='*'
          element={
            <InquiryTemplateEditor
              isLoading={isLoading}
              inquiryTemplate={inquiryTemplate}
              inquiryTemplateVersion={inquiryTemplateVersion}
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              partialInquiryTemplateVersions={shownPartialInquiryTemplateVersions}
              locale={locale}
              basePath={basePath}
              submissionWorkflowErrors={submissionWorkflowErrors}
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              submissionComponentErrors={submissionComponentErrors}
              matchRequirementProfileMap={inquiryTemplateTreeData.schema.matchRequirementProfiles}
              moduleOptions={moduleOptions}
              modulePreviewMap={modulePreviewMap}
              isShowingInquiryTemplateVersions={isShowingInquiryTemplateVersions}
              isFormPristine={!inquiryTemplateVersionUpdated}
              keydownShortcutsEnabled={keydownShortcutsEnabled}
              workflowSchema={workflowSchema}
              versionDiff={versionDiff}
              currentSchema={schema}
              comparedSchema={comparedSchema}
              comparedVersion={comparedPublishedVersion}
              loadingComparedVersion={loadingComparedVersion}
              loadingVersionDiff={loadingVersionDiff}
              inputStep={inputStep}
              initialValues={initialValues}
              refreshWorkflowSchema={refreshWorkflowSchema}
              validationErrors={validationErrors}
              allowSavingInvalidItvDraft={allowSavingInvalidItvDraft}
              handleAddLocalDraftVersion={handleAddLocalDraftVersion}
              handleDiscardDraft={handleDiscard}
              handleSelectVersion={handleSelectVersion}
              handleSetLocale={handleSetLocale}
              handleLoadModulePreview={handleLoadModulePreview}
              handleLoadModule={handleLoadModule}
              handleLoadVersionDiff={handleLoadVersionDiff}
              handleLoadComparedVersion={handleLoadComparedVersion}
              handleLoadStepLocalizedStrings={handleLoadStepLocalizedStrings}
              handleUpdateStoredVerificationTemplates={handleUpdateStoredVerificationTemplates}
              updateValidationErrors={updateValidationErrors}
              hideInquiryTemplateVersions={hideInquiryTemplateVersions}
              onSubmit={updateInquiryTemplate}
              setRefreshWorkflowSchema={setRefreshWorkflowSchema}
            />
          }
        />

        <Route path='*' element={<Navigate to='/dashboard/inquiry-templates' replace />} />
      </SentryRoutes>
      <div id={INQUIRY_TEMPLATE_EDITOR_SHOW_VIEW_ID} />
    </>
  );
};

export default DashboardInquiryTemplatesShowView;
